import { useMemo } from 'react'

import { Box, Text, theme } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import {
  PaymentRequestStatus,
  getPaymentRequestStatusColor,
  getPaymentRequestStatusLabel,
} from 'modules/compensations/lib/constants'

type ProgressSegment = {
  amount: number
  color: string
}

type SegmentedProgressBarProps = {
  segments: ProgressSegment[]
  total: number
}

const Segment = ({ percentage, color }: { percentage: number; color: string }) => {
  return (
    <Box
      height="full"
      borderRadius={{ all: 'abs100' }}
      style={{ width: `${percentage}%`, backgroundColor: color }}
    />
  )
}

const ColorDot = ({ color }: { color: string }) => {
  return (
    <Box
      width="s12"
      height="s12"
      borderRadius={{ all: 'abs100' }}
      style={{ backgroundColor: color }}
    />
  )
}

const SegmentedProgressBar = ({ segments, total }: SegmentedProgressBarProps) => {
  const legendSegments = useMemo(
    () => [
      {
        color: getPaymentRequestStatusColor(PaymentRequestStatus.PAID),
        label: getPaymentRequestStatusLabel(PaymentRequestStatus.PAID),
      },
      {
        color: getPaymentRequestStatusColor(PaymentRequestStatus.IN_PROGRESS),
        label: getPaymentRequestStatusLabel(PaymentRequestStatus.IN_PROGRESS),
      },
      {
        color: getPaymentRequestStatusColor(PaymentRequestStatus.PENDING_TO_PAY),
        label: i18n.t('compensations.payments.statuses.pending_to_send'),
      },
    ],
    []
  )

  return (
    <Box flexDirection="column" gap="s8">
      <Box gap="s8">
        <Box width="full" height="s8" style={{ overflow: 'hidden' }} flexDirection="row" gap="s4">
          {segments.length === 0 && <Segment percentage={100} color={theme.colors.grey200} />}

          {segments.map((segment, index) => {
            const percentage = (segment.amount / total) * 100

            return <Segment key={index} percentage={percentage} color={segment.color} />
          })}
        </Box>

        <Box flexDirection="row" gap="s16">
          {legendSegments.map((segment) => (
            <Box key={segment.label} flexDirection="row" alignItems="center" gap="s6">
              <ColorDot color={segment.color} />

              <Text.Normal size="300" color="textPrimary">
                {segment.label}
              </Text.Normal>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default SegmentedProgressBar
